/**
 * stufen-labels.ts — kanonische ASR-A3.5-Stufen-Labels (0..3).
 *
 * confirmed_level ist 0..3 (backend-contract). Dieses Modul ist die
 * EINZIGE source-of-truth für user-sichtbare Stufenbezeichnungen und
 * Farb-Tokens. Alle Render-Stellen (HeatBadge, Dropdowns, Hero, Vorschau)
 * ziehen ausschließlich von hier.
 *
 * Stufen nach ASR A3.5 / BAuA (DB-Werte 0-3, NICHT 1-4):
 *   0  Normalbereich  ≤ 26 °C
 *   1  Soll           > 26 °C   (empfohlene Maßnahmen, bei warmer Außentemp.)
 *   2  Muss           > 30 °C   (Pflicht-Maßnahmen)
 *   3  Kritisch       > 35 °C   (Betrieb einschränken/prüfen)
 */

export type ConfirmedLevel = 0 | 1 | 2 | 3;

export interface StufeInfo {
  /** Kurzbezeichnung für Badges, Tabellen */
  short: string;
  /** Zusatz-Information für Tooltips, aria-label, Erklärtext */
  zusatz: string;
  /** Temperaturschwelle als Anzeigestring */
  schwelle: string;
  /**
   * CSS-custom-property-name für den Hintergrund (ohne var()).
   * Verwendet heat-1..heat-4 tokens aus dem Design-System.
   * heat-1 = grün (Normalbereich), heat-4 = rot (Kritisch).
   */
  colorToken: `--color-heat-${1 | 2 | 3 | 4}`;
}

export const STUFEN_INFO: Record<ConfirmedLevel, StufeInfo> = {
  0: {
    short: "Normalbereich",
    zusatz: "",
    schwelle: "≤ 26 °C",
    colorToken: "--color-heat-1",
  },
  1: {
    short: "Soll",
    zusatz: "empfohlene Maßnahmen",
    schwelle: "> 26 °C",
    colorToken: "--color-heat-2",
  },
  2: {
    short: "Muss",
    zusatz: "Pflicht-Maßnahmen",
    schwelle: "> 30 °C",
    colorToken: "--color-heat-3",
  },
  3: {
    short: "Kritisch",
    zusatz: "Betrieb einschränken/prüfen",
    schwelle: "> 35 °C",
    colorToken: "--color-heat-4",
  },
};

/** Kurzlabel für Stufe N (Fallback für unbekannte Werte: "Stufe N"). */
export function stufeShort(level: number): string {
  return STUFEN_INFO[level as ConfirmedLevel]?.short ?? `Stufe ${level}`;
}

/** Band-String für Chart-Untertitel: "Stufe 0 · ≤ 26 °C" */
export function stufeBand(level: ConfirmedLevel): string {
  const info = STUFEN_INFO[level];
  return `Stufe ${level} · ${info.schwelle}`;
}

/**
 * Dropdown-Optionen für Stufen-Override-Selects (0-3, DB-Wert-aligned).
 * Format: "0 — Normalbereich (≤ 26 °C)"
 */
export const STUFEN_SELECT_OPTIONS: ReadonlyArray<{
  value: ConfirmedLevel;
  label: string;
}> = [0, 1, 2, 3].map((level) => {
  const info = STUFEN_INFO[level as ConfirmedLevel];
  return {
    value: level as ConfirmedLevel,
    label: `${level} — ${info.short} (${info.schwelle})`,
  };
});

// ─── Period helpers ────────────────────────────────────────────────────────────

/**
 * ExportPeriod — API-contract (GET /v1/me/export.* + /v1/traeger/export.*).
 *
 *  "month"    → laufender Kalendermonat (1. des Monats .. jetzt)  [DEFAULT]
 *  "YYYY-MM"  → spezifischer Kalendermonat, z.B. "2026-05"
 *  "quarter"  → letztes Quartal (rolling 90d)
 *  "year"     → letztes Jahr (rolling 365d)
 *  "season"   → Hitzesaison Jun–Sep
 *  "all"      → gesamter Zeitraum
 */
export type ExportPeriod = "month" | "quarter" | "year" | "season" | "all" | `${number}-${string}`;

/**
 * Gibt die letzten N Kalendermonate zurück (neuester zuerst).
 * Anzahl = 14 damit mind. 1 vollständiges Vorjahr sichtbar ist.
 * Format: { value: "YYYY-MM", label: "Mai 2026 (aktuell)" | "April 2026" }
 */
export function recentMonths(count = 14): { value: string; label: string }[] {
  const formatter = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
  const now = new Date();
  const results: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const value = `${year}-${month}`;
    const label = formatter.format(d) + (i === 0 ? " (aktuell)" : "");
    results.push({ value, label });
  }
  return results;
}

/**
 * Gibt den API-period-string für den aktuellen Monat zurück.
 * Gemäß API-Contract wird "month" als laufender Kalendermonat gesendet.
 */
export function currentMonthPeriod(): string {
  return "month";
}

/**
 * Gibt das PeriodLabel (für PDF/XLSX-Kopf) für einen period-string zurück.
 * Entspricht dem API-contract PeriodLabel.
 */
export function periodLabel(period: string): string {
  if (period === "month") {
    const formatter = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" });
    return formatter.format(new Date());
  }
  if (period === "quarter") return "Letztes Quartal";
  if (period === "year") return "Letztes Jahr";
  if (period === "season") {
    return `Hitzesaison ${new Date().getFullYear()}`;
  }
  if (period === "all") return "Gesamter Zeitraum";
  // YYYY-MM format
  const match = period.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const d = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    return new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(d);
  }
  return period;
}
